import apiService from '../services/APIService';
import Response from '../modules/classes/Response';

class ShelfController {
  constructor(apiService) {
    this.apiService = apiService;
  }

  getService() {
    return this.apiService ? this.apiService : apiService;
  }

  /**
   * Get all collections for the current user
   * @param {string} type - 'grail' | 'shelf' | 'database'
   * @returns {Promise} API response with collections data
   */
  async getAllCollections(type = 'grail') {
    return new Promise((resolve, reject) => {
      this.getService().get(`/shelf/collections?type=${type}`).then((res) => {
        resolve(Response.parse(res));
      }).catch((err) => {
        reject(err);
      });
    });
  }

  /**
   * Get collections with filter
   * @param {Object} filter - Filter parameters (type, etc.)
   * @param {string} userId - Optional user ID
   * @returns {Promise} API response with collections data
   */
  async getCollections(filter = null, userId = null) {
    return new Promise((resolve, reject) => {
      const endpoint = userId ? `/shelf/collections/${userId}` : '/shelf/collections';
      const params = filter || {};
      
      this.getService().get(endpoint, { params }).then((res) => {

        resolve(Response.parse(res));
      }).catch((err) => {
        reject(err);
      });
    });
  }

  /**
   * Get collections for a specific user
   * @param {string} userId - User ID
   * @returns {Promise} API response with user collections
   */
  async getUserCollections(userId) {
    return new Promise((resolve, reject) => {
      this.getService().get(`/shelf/collections/${userId}`).then((res) => {
        resolve(Response.parse(res));
      }).catch((err) => {
        reject(err);
      });
    });
  }

  /**
   * Get shelf collection by type (owned, wished, ordered)
   * @param {string} collection - Collection type
   * @param {number} page - Page number
   * @param {number} pageSize - Items per page
   * @param {string|null} userId - Optional target userId
   * @returns {Promise} API response with collection data
   */
  async getShelfByCollection(collection, page = 1, pageSize = 10, userId = null) {
    return new Promise((resolve, reject) => {
      const endpoint = userId
        ? `/shelf/collection/${userId}/${collection}?page=${page}&pageSize=${pageSize}`
        : `/shelf/collection/${collection}?page=${page}&pageSize=${pageSize}`;

      this.getService().get(endpoint).then((res) => {
        resolve(Response.parse(res));
      }).catch((err) => {
        reject(err);
      });
    });
  }

  /**
   * Add figure to shelf
   * @param {string} figureId - Figure ID
   * @param {string} collection - Collection type (owned, wished, ordered)
   * @returns {Promise} API response
   */
  async addToShelf(figureId, collection) {
    return new Promise((resolve, reject) => {
      this.getService().post(`/shelf/${figureId}`, { collection }).then((res) => {
        resolve(Response.parse(res));
      }).catch((err) => {
        reject(err);
      });
    });
  }

  /**
   * Remove figure from shelf
   * @param {string} figureId - Figure ID
   * @returns {Promise} API response
   */
  async removeFromShelf(figureId) {
    return new Promise((resolve, reject) => {
      this.getService().delete(`/shelf/${figureId}`).then((res) => {
        resolve(Response.parse(res));
      }).catch((err) => {
        reject(err);
      });
    });
  }

  /**
   * Arrange shelf items
   * @param {Array} arrangements - Array of {collectionId, position}
   * @returns {Promise} API response
   */
  async arrangeShelf(arrangements) {
    return new Promise((resolve, reject) => {
      this.getService().patch('/shelf', arrangements).then((res) => {
        resolve(Response.parse(res));
      }).catch((err) => {
        reject(err);
      });
    });
  }

  /**
   * Get grail items
   * @param {string} collection - Collection type
   * @returns {Promise} API response with grail data
   */
  async getGrail(collection) {
    return new Promise((resolve, reject) => {
      this.getService().get(`/shelf/grail?collection=${collection}`).then((res) => {
        resolve(Response.parse(res));
      }).catch((err) => {
        reject(err);
      });
    });
  }

  /**
   * Get user's grail
   * @param {string} userId - User ID
   * @returns {Promise} API response with grail data
   */
  async getUserGrail(userId) {
    return new Promise((resolve, reject) => {
      this.getService().get(`/shelf/grail/${userId}`).then((res) => {
        resolve(Response.parse(res));
      }).catch((err) => {
        reject(err);
      });
    });
  }

  /**
   * Add figure to grail
   * @param {string} collectionId - Collection ID
   * @param {string} collection - Collection type
   * @param {number} grailRank - Grail rank (optional)
   * @returns {Promise} API response
   */
  async addToGrail(collectionId, collection, grailRank = null) {
    return new Promise((resolve, reject) => {
      this.getService().post(`/shelf/${collectionId}/grail`, { 
        collection, 
        grailRank 
      }).then((res) => {
        resolve(Response.parse(res));
      }).catch((err) => {
        reject(err);
      });
    });
  }

  /**
   * Arrange grail items
   * @param {Array} arrangements - Array of {collectionId, grailRank}
   * @returns {Promise} API response
   */
  async arrangeGrail(arrangements) {
    return new Promise((resolve, reject) => {
      this.getService().patch('/shelf/grail', { arrangements }).then((res) => {
        resolve(Response.parse(res));
      }).catch((err) => {
        reject(err);
      });
    });
  }

  /**
   * Change collection status of a grail item (e.g. wished -> owned)
   * @param {string} collectionId - Collection entry ID
   * @param {string} collection - New collection status (owned, wished, ordered)
   * @returns {Promise} API response
   */
  async changeGrailCollection(collectionId, collection) {
    return new Promise((resolve, reject) => {
      this.getService().patch(`/shelf/grail/${collectionId}/collection`, { collection }).then((res) => {
        resolve(Response.parse(res));
      }).catch((err) => {
        reject(err);
      });
    });
  }

  /**
   * Remove a figure from grail (sets grailRank to null)
   * @param {string} collectionId - Collection entry ID
   * @returns {Promise} API response
   */
  async removeFromGrail(collectionId) {
    return new Promise((resolve, reject) => {
      this.getService().delete(`/shelf/grail/${collectionId}`).then((res) => {
        resolve(Response.parse(res));
      }).catch((err) => {
        reject(err);
      });
    });
  }

  /**
   * Get grail statistics for a specific figure
   * Returns count of users who have grailed this figure
   * @param {string} figureId - Figure ID
   * @returns {Promise} API response with grail count
   */
  async getFigureGrailStats(figureId) {
    return new Promise((resolve, reject) => {
      this.getService().get(`/shelf/stats/grail/${figureId}`).then((res) => {
        resolve(Response.parse(res));
      }).catch((err) => {
        reject(err);
      });
    });
  }

  /**
   * Get collection statistics for a specific figure
   * Returns counts for owned, ordered, wished, and grailed
   * @param {string} figureId - Figure ID
   * @returns {Promise} API response with collection stats
   */
  async getFigureCollectionStats(figureId) {
    return new Promise((resolve, reject) => {
      this.getService().get(`/shelf/stats/collection/${figureId}`).then((res) => {
        resolve(Response.parse(res));
      }).catch((err) => {
        reject(err);
      });
    });
  }

  /**
   * Get friends (followers/following) statistics for a specific figure
   * Requires auth; returns counts for owned, ordered, wished (and optionally grailed)
   * @param {string} figureId - Figure ID
   * @returns {Promise} API response with friends stats
   */
  async getFigureFriendsStats(figureId) {
    return new Promise((resolve, reject) => {
      this.getService().get(`/shelf/stats/friends/${figureId}`).then((res) => {
        resolve(Response.parse(res));
      }).catch((err) => {
        reject(err);
      });
    });
  }

  /**
   * Set or clear the shelf cover image for a collection entry.
   * Pass null to reset to the figure's default image.
   * @param {string} collectionId
   * @param {string|null} customImageUrl
   */
  async updateShelfItemImage(collectionId, customImageUrl) {
    return new Promise((resolve, reject) => {
      this.getService()
        .patch(`/shelf/collection/${collectionId}/image`, { customImageUrl: customImageUrl ?? null })
        .then((res) => { resolve(Response.parse(res)); })
        .catch((err) => { reject(err); });
    });
  }
}

export default ShelfController;
